import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import { Sankey, Rectangle, Layer } from "recharts";
import { colors2 } from "oolib";
import { dataVizColors } from "../common/colors";

interface SankeyNode {
  name: string;
  level: number;
}

interface SankeyLink {
  source: number;
  target: number;
  value: number;
}

interface SankeyData {
  nodes: SankeyNode[];
  links: SankeyLink[];
}

interface SankeyChartProps {
  data: SankeyData;
  colorIdx?: number;
  style?: React.CSSProperties;
}

const MIN_HEIGHT = 150;
const NODE_WIDTH = 12;
const NODE_PADDING = 24;
const LINK_CURVATURE = 0.5;
const MARGIN = { top: 20, right: 160, bottom: 20, left: 20 };
const MIN_WIDTH_PER_LEVEL = 200;

// Assign colors by cycling through the dataviz palette per node index
const getNodeColor = (nodeIndex: number, colorIdx: number) =>
  dataVizColors[(colorIdx + nodeIndex) % dataVizColors.length];

// Custom node renderer: colored rectangle with a label to the right
const CustomNode = ({
  x,
  y,
  width,
  height,
  index,
  payload,
  colorIdx,
}: any) => {
  const fill = getNodeColor(index, colorIdx);
  const name = payload?.name ?? "";
  const value = payload?.value ?? 0;

  return (
    <Layer key={`node-${index}`}>
      <Rectangle
        x={x}
        y={y}
        width={width}
        height={height}
        fill={fill}
        fillOpacity={1}
        rx={3}
        ry={3}
      />
      <text
        x={x + width + 8}
        y={y + height / 2}
        textAnchor="start"
        dominantBaseline="central"
        className="UI_CAPTION"
        style={{ fill: colors2.grey80 }}
      >
        {name}
      </text>
      <text
        x={x + width + 8}
        y={y + height / 2 + 16}
        textAnchor="start"
        dominantBaseline="central"
        className="UI_CAPTION_SEMIBOLD_DF"
        style={{ fill: colors2.grey50 }}
      >
        {value.toLocaleString()}
      </text>
    </Layer>
  );
};

// Custom link renderer: semi-transparent path colored by source node
const CustomLink = (props: any) => {
  const {
    sourceX,
    targetX,
    sourceY,
    targetY,
    sourceControlX,
    targetControlX,
    linkWidth,
    index,
    payload,
  } = props;

  const sourceNodeIndex = payload?.source?.index ?? 0;
  const fill = getNodeColor(
    sourceNodeIndex,
    props.colorIdx ?? 0
  );

  return (
    <Layer key={`link-${index}`}>
      <path
        d={`
          M${sourceX},${sourceY + linkWidth / 2}
          C${sourceControlX},${sourceY + linkWidth / 2}
            ${targetControlX},${targetY + linkWidth / 2}
            ${targetX},${targetY + linkWidth / 2}
          L${targetX},${targetY - linkWidth / 2}
          C${targetControlX},${targetY - linkWidth / 2}
            ${sourceControlX},${sourceY - linkWidth / 2}
            ${sourceX},${sourceY - linkWidth / 2}
          Z
        `}
        fill={fill}
        fillOpacity={0.25}
        stroke={fill}
        strokeWidth={0}
      />
    </Layer>
  );
};

export const SankeyChart: React.FC<SankeyChartProps> = ({
  data,
  colorIdx = 0,
  style = {},
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const updateDimensions = () => {
      const { width, height } = el.getBoundingClientRect();
      setDimensions((prev) => {
        if (Math.abs(prev.width - width) > 1 || Math.abs(prev.height - height) > 1) {
          return { width, height };
        }
        return prev;
      });
    };

    updateDimensions();

    const observer = new ResizeObserver(updateDimensions);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Height driven by the densest column (most nodes stacked vertically at one level)
  const chartHeight = useMemo(() => {
    const nodesPerLevel: Record<number, number> = {};
    data.nodes.forEach((n) => {
      const level = n.level || 0;
      nodesPerLevel[level] = (nodesPerLevel[level] || 0) + 1;
    });
    const maxNodesInColumn = Math.max(...Object.values(nodesPerLevel), 1);
    const estimated =
      maxNodesInColumn * (NODE_PADDING + 20) + MARGIN.top + MARGIN.bottom;
    return Math.max(MIN_HEIGHT, estimated);
  }, [data.nodes]);

  // Calculate minimum width based on number of levels so columns aren't squished
  const numLevels = useMemo(() => {
    return data.nodes.reduce((max, n) => Math.max(max, n.level || 0), 0) + 1;
  }, [data.nodes]);

  const nodeRenderer = useCallback(
    (props: any) => <CustomNode {...props} colorIdx={colorIdx} />,
    [colorIdx]
  );

  const linkRenderer = useCallback(
    (props: any) => <CustomLink {...props} colorIdx={colorIdx} />,
    [colorIdx]
  );

  if (!data.nodes.length || !data.links.length) {
    return <div>No data available for Sankey chart</div>;
  }

  const containerWidth = dimensions.width || 600;
  const minChartWidth = numLevels * MIN_WIDTH_PER_LEVEL + MARGIN.left + MARGIN.right;
  const chartWidth = Math.max(containerWidth, minChartWidth);

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        minHeight: MIN_HEIGHT,
        overflow: "auto",
        ...style,
      }}
    >
      {dimensions.width > 0 && (
        <Sankey
          width={chartWidth}
          height={chartHeight}
          data={data}
          nodeWidth={NODE_WIDTH}
          nodePadding={NODE_PADDING}
          linkCurvature={LINK_CURVATURE}
          iterations={64}
          margin={MARGIN}
          node={nodeRenderer}
          link={linkRenderer}
          align="left"
          sort
        />
      )}
    </div>
  );
};
